import { Box, Tooltip } from "@mui/material";
import clsx from "clsx";
import type React from "react";
import { Link, useHistory } from "react-router-dom";

import { type Notification, NotificationPriority } from "src/types/notification";

import { NotificationIcon } from "./NotificationIcon";
import {
    getNotificationLinkText,
    getNotificationRedirection,
    notificationDateToDateString,
    useGetNotificationText,
} from "./Notifications.logic";

import { useNotificationStyles } from "./Notifications.styles";

import { useTranslation } from "src/design-system/core/TranslationContext";
import { useLinkButtonStyles } from "src/hooks/styles/useLinkButtonStyles";
import { NOTIFICATIONS_TRANSLATION_NAMESPACE } from "./config";

export const NOTIFICATIONS_ELEMENT_TK = "NOTIFICATIONS_ELEMENT_TK";

interface NotificationElementProps {
    notification: Notification;
    onLinkClick: (notification: Notification, hasToNotClose?: boolean) => () => Promise<void>;
}

export const NotificationElement: React.FC<NotificationElementProps> = ({
    notification,
    onLinkClick,
}) => {
    const { t } = useTranslation(
        `${NOTIFICATIONS_TRANSLATION_NAMESPACE}:${NOTIFICATIONS_ELEMENT_TK}`
    );
    const classes = useNotificationStyles();
    const linkButtonClasses = useLinkButtonStyles();
    const history = useHistory();
    const parameters: any = notification.parameters;
    const getNotificationText = useGetNotificationText();

    return (
        <Box
            display="flex"
            className={clsx(
                {
                    [classes.errorItem]: notification.priority === NotificationPriority.Error,
                    [classes.item]: notification.priority !== NotificationPriority.Error,
                },
                classes.notification
            )}
        >
            <NotificationIcon priority={notification.priority} />
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="center"
                flex="2"
                onClick={() => {
                    onLinkClick(notification)();
                    history.push(getNotificationRedirection(history, notification));
                }}
            >
                <div className={classes.text}>
                    {parameters.posName && (
                        <Box fontWeight="bold" display="inline">
                            {`${parameters.posName} :`}&nbsp;
                        </Box>
                    )}
                    {getNotificationText(notification)}
                </div>
                <Link
                    onClick={onLinkClick(notification)}
                    to={getNotificationRedirection(history, notification)}
                    className={linkButtonClasses.link}
                >
                    {getNotificationLinkText(notification)}
                </Link>
            </Box>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-end"
                flexDirection="column"
                className={classes.date}
                onClick={(event) => {
                    onLinkClick(notification, true)();
                    event.stopPropagation();
                    event.preventDefault();
                }}
            >
                {!notification.seenDateUTC && (
                    <Tooltip
                        title={
                            notification.priority === NotificationPriority.Error
                                ? t("action-needed-to-mark-as-read")
                                : t("mark-as-read")
                        }
                    >
                        <div className={classes.newBackground}>
                            <div className={classes.new} />
                        </div>
                    </Tooltip>
                )}
                {notification.seenDateUTC && (
                    <Tooltip title={t("mark-as-unread")}>
                        <div className={classes.oldBackground}>
                            <div className={classes.old} />
                        </div>
                    </Tooltip>
                )}
                <div>{notificationDateToDateString(notification.createdDateUTC)}</div>
            </Box>
        </Box>
    );
};
